/**
 * Maps normalised [0,1] coordinates of the intrinsic video frame to CSS pixels of the element box,
 * accounting for object-fit letterboxing/pillarboxing. Pure: tested in test/geometry.test.ts.
 */
export interface Size {
  w: number;
  h: number;
}
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function contentRect(box: Size, intrinsic: Size, fit: 'contain' | 'cover' = 'contain'): Rect {
  if (box.w <= 0 || box.h <= 0 || intrinsic.w <= 0 || intrinsic.h <= 0) return { x: 0, y: 0, w: 0, h: 0 };
  const scale = fit === 'contain' ? Math.min(box.w / intrinsic.w, box.h / intrinsic.h) : Math.max(box.w / intrinsic.w, box.h / intrinsic.h);
  const w = intrinsic.w * scale;
  const h = intrinsic.h * scale;
  return { x: (box.w - w) / 2, y: (box.h - h) / 2, w, h };
}

export function normToBox(p: { x: number; y: number }, rect: Rect): { x: number; y: number } {
  return { x: rect.x + p.x * rect.w, y: rect.y + p.y * rect.h };
}

/** Backing-store size for a crisp canvas at the given devicePixelRatio. */
export function canvasBacking(box: Size, dpr: number): Size {
  const r = Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
  return { w: Math.max(1, Math.round(box.w * r)), h: Math.max(1, Math.round(box.h * r)) };
}

/**
 * Unit normal pointing in the A_TO_B direction: the right-hand side of segment a→b in screen
 * coordinates (y grows downward). For a left-to-right horizontal line this points down.
 */
export function crossingNormal(a: { x: number; y: number }, b: { x: number; y: number }): { x: number; y: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: -dy / len, y: dx / len };
}
